import { memo, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { KentuckyKYC, SeonPlugin } from "@leapfinancial/kentucky-sdk";
import { createKentuckyPostHogBridge } from "../analytics";
import { config } from "../config";
import { APP_ROUTES } from "../constants";
import { useSessionStore } from "../store/useSessionStore";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { useClaimLink } from "../hooks/useClaimLink";
import { useKentuckySdkStyles } from "../hooks/useKentuckySdkStyles";
import { forgetCipTicketId, rememberCipTicketId } from "../utils/chunkReload";
import { shouldPromptPhone } from "../utils/phoneGate";
import { ensureSmsConsent } from "../components/notifications/smsConsent";

interface CipPageLocationState {
  cipTicketId: string;
}

/**
 * Embedded Kentucky CIP/KYC screen. On completion the sender returns to minting,
 * which creates the payout-claim link for the recipient.
 */
export const CipPage = memo(() => {
  // Scope SDK CSS to this screen only (removed on navigate away).
  useKentuckySdkStyles();
  // Re-claim the link so the session survives the SDK redirect.
  useClaimLink();
  const navigate = useNavigate();
  const { redirectTo } = useRouteWithToken();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { session } = useSessionStore();

  const state = location.state as CipPageLocationState | null;
  const cipTicketId = rememberCipTicketId(state?.cipTicketId);

  const partnerTheme = session.data?.partner?.theme;
  const userStatus = session.data?.userStatus;
  const canOriginate = !userStatus?.blocked && !userStatus?.suspended;
  const plugins = useMemo(
    () => [createKentuckyPostHogBridge(), SeonPlugin()],
    [],
  );

  if (!canOriginate) {
    redirectTo(APP_ROUTES.HOME_PAGE, { replace: true });
    return null;
  }

  if (!cipTicketId) {
    navigate(APP_ROUTES.ERROR_PAGE, {
      state: {
        title: t("error.title"),
        subTitle: null,
        message: t("error.tryAgainLater"),
        btnText: t("common.retry"),
        isOut: false,
        pageUrl: APP_ROUTES.RECIPIENT_PAGE,
      },
    });
    return null;
  }

  return (
    <KentuckyKYC
      ticketId={cipTicketId}
      apiBaseUrl={config.KENTUCKY_API_BASE_URL}
      googleMapsApiKey={config.GOOGLE_MAP_API_KEY}
      awsRegion={config.AWS_REGION || "us-east-1"}
      theme={partnerTheme}
      plugins={plugins}
      onComplete={() => {
        forgetCipTicketId();
        const current = useSessionStore.getState().session.data;
        const prompt = shouldPromptPhone(current, current?.linkId);
        if (prompt) {
          redirectTo(APP_ROUTES.PHONE_PAGE, {
            replace: true,
            state: { next: APP_ROUTES.MINTING_PAGE },
          });
          return;
        }
        const linkId = current?.linkId;
        void (linkId
          ? ensureSmsConsent(linkId, i18n.language?.slice(0, 2) || "en")
          : Promise.resolve()
        ).then(() => {
          redirectTo(APP_ROUTES.MINTING_PAGE, {
            replace: true,
            state: { next: APP_ROUTES.MINTING_PAGE },
          });
        });
      }}
      onError={(error) => {
        navigate(APP_ROUTES.ERROR_PAGE, {
          state: {
            title: t("error.title"),
            subTitle: null,
            message:
              error.message || t("error.tryAgainLater"),
            btnText: t("common.retry"),
            isOut: false,
            pageUrl: APP_ROUTES.RECIPIENT_PAGE,
          },
        });
      }}
    />
  );
});
